import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { CheckCircle, Calculator, FileText, Calendar, Shield } from 'lucide-react';

const TaxPreparationPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-[#3E2723] mb-6">
            Tax Preparation Services
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl">
            Expert tax preparation and filing services ensuring compliance with all US tax regulations while maximizing your deductions and minimizing liabilities.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Comprehensive Tax Solutions</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                At Trueledger, we provide comprehensive tax preparation services for businesses and individuals. Our experienced tax professionals stay current with ever-changing tax laws to ensure you remain compliant while taking advantage of every available deduction and credit.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                We serve startups, SMBs, and established corporations, providing strategic tax planning throughout the year - not just at tax time. Our proactive approach helps you make informed decisions that optimize your tax position.
              </p>
              <p className="text-gray-600 leading-relaxed">
                From federal and state income taxes to sales tax and payroll taxes, we handle all aspects of tax compliance, giving you peace of mind and more time to focus on your business.
              </p>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=600&h=400&fit=crop" 
                alt="Tax Preparation Services"
                className="rounded shadow-lg w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Key Benefits */}
          <div className="mb-16 bg-stone-50 rounded-lg p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Choose Our Tax Services?</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <BenefitCard 
                icon={Calculator}
                title="Maximize Deductions"
                description="Identify all eligible deductions to minimize your tax burden"
              />
              <BenefitCard 
                icon={Shield}
                title="Compliance Assured"
                description="Stay compliant with federal, state, and local tax laws"
              />
              <BenefitCard 
                icon={Calendar}
                title="Year-Round Planning"
                description="Strategic tax planning throughout the year, not just tax season"
              />
              <BenefitCard 
                icon={FileText}
                title="Audit Support"
                description="Full support and representation in case of tax audits"
              />
            </div>
          </div>

          {/* Services Included */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Comprehensive Tax Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ServiceItem text="Federal income tax preparation and filing" />
              <ServiceItem text="State and local tax returns" />
              <ServiceItem text="Corporate tax returns (Form 1120, 1120S)" />
              <ServiceItem text="Partnership tax returns (Form 1065)" />
              <ServiceItem text="Individual tax returns (Form 1040)" />
              <ServiceItem text="FBAR Compliance" />
              <ServiceItem text="Payroll tax filing and compliance" />
              <ServiceItem text="Quarterly estimated tax payments" />
              <ServiceItem text="Tax planning and strategy" />
              <ServiceItem text="Multi-state tax compliance" />
              <ServiceItem text="IRS audit representation" />
              <ServiceItem text="Tax notices and correspondence resolution" />
            </div>
          </div>

          {/* Tax Planning */}
          <div className="mb-16 bg-stone-50 rounded-lg p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Proactive Tax Planning</h2>
            <p className="text-gray-600 mb-8">
              Our tax planning services help you make strategic decisions throughout the year to optimize your tax position:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PlanningItem title="Entity Structure Analysis" description="Choose the right business structure to minimize taxes" />
              <PlanningItem title="Timing Strategies" description="Optimize income and expense timing for tax benefits" />
              <PlanningItem title="Retirement Planning" description="Maximize tax-advantaged retirement contributions" />
              <PlanningItem title="Investment Strategy" description="Implement tax-efficient investment approaches" />
              <PlanningItem title="Credits & Incentives" description="Identify applicable tax credits and incentives" />
              <PlanningItem title="Loss Harvesting" description="Strategic use of losses to offset gains" />
            </div>
          </div>

          {/* Timeline */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Tax Preparation Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <ProcessStep 
                number="01"
                title="Document Collection"
                description="Gather all necessary financial documents and records"
              />
              <ProcessStep 
                number="02"
                title="Preparation & Review"
                description="Prepare returns and review for accuracy and optimization"
              />
              <ProcessStep 
                number="03"
                title="Client Approval"
                description="Review returns with you and address any questions"
              />
              <ProcessStep 
                number="04"
                title="Filing & Follow-up"
                description="File returns electronically and handle all follow-up"
              />
            </div>
          </div>

          {/* CTA */}
          <div className="bg-[#3E2723] rounded-lg p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              Ready to simplify your taxes?
            </h2>
            <p className="text-stone-200 mb-8 text-lg">
              Let our experts handle your tax preparation and planning.
            </p>
            <a href="mailto:hello@trueledger.com?subject=Tax%20Preparation%20Services%20Inquiry&body=Hi%20Trueledger%20Team,%0A%0AI%20am%20interested%20in%20your%20tax%20preparation%20services.%0A%0AThank%20you.">
              <Button className="bg-white text-[#3E2723] hover:bg-stone-100 px-10 py-6 text-lg shadow-lg">
                Get Started Today
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ServiceItem = ({ text }) => (
  <div className="flex items-start space-x-3">
    <CheckCircle className="w-6 h-6 text-[#3E2723] flex-shrink-0 mt-0.5" />
    <span className="text-gray-700">{text}</span>
  </div>
);

const BenefitCard = ({ icon: Icon, title, description }) => (
  <div className="text-center">
    <div className="w-16 h-16 bg-[#3E2723] rounded-full flex items-center justify-center mx-auto mb-4">
      <Icon className="w-8 h-8 text-white" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm">{description}</p>
  </div>
);

const ProcessStep = ({ number, title, description }) => (
  <div>
    <div className="text-5xl font-bold text-[#3E2723] mb-3">{number}</div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

const PlanningItem = ({ title, description }) => (
  <div className="bg-white border border-stone-200 rounded-lg p-4">
    <h4 className="text-lg font-semibold text-[#3E2723] mb-2">{title}</h4>
    <p className="text-gray-600 text-sm">{description}</p>
  </div>
);

export default TaxPreparationPage;
